import React, { useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, Filter, Mail, Sparkles } from 'lucide-react';

import unDays from '../../data/un-observance-days.json';
import campaignIdeasData from '../../data/un-campaign-ideas.json';
import CampaignIdeasCarousel from './CampaignIdeasCarousel';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function UNCampaignPlanner() {
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [selectedDay, setSelectedDay] = useState<any>(null);
  const [filters, setFilters] = useState({ format: '' });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  // Expanded state for campaign idea cards (by index)
  const [expandedCards, setExpandedCards] = useState<{[key: string]: boolean}>({});


  // Smart filter options from campaignIdeasData
  const allFormats = Array.from(new Set(campaignIdeasData.flatMap((d: any) => d.campaignIdeas.map((c: any) => c.format)))).sort();

  // Filtered campaign ideas for selected day
  const getFilteredCampaigns = (day: any) => {
    const campaignBlock = campaignIdeasData.find((d: any) => d.name === day?.name);
    if (!campaignBlock) return [];
    return campaignBlock.campaignIdeas.filter((c: any) =>
      (!filters.format || c.format === filters.format)
    );
  };

  // Placeholder email send logic
  const handleEmailSend = (e: React.FormEvent) => {
    e.preventDefault();
    setTimeout(() => setEmailSent(true), 800); // Fake sending
    setTimeout(() => { setShowEmailModal(false); setEmail(''); setEmailSent(false); }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 pb-16">
      <div className="max-w-4xl mx-auto px-4 pt-12">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-2">
            <span className="inline-flex items-center bg-white rounded-full px-4 py-2 shadow text-lg font-semibold gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              UN Observance Day Campaign Planner
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black mb-2 mt-4">Turn awareness into action. All year round.</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Ever miss an impact moment like World Environment Day or Pride Month and scramble for ideas last minute? Not anymore.<br />
            This interactive, AI-powered calendar maps every major UN Observance Day to campaign ideas, ready-to-send copy, and more.
          </p>
        </div>

        {/* Month Picker */}
        <div className="flex justify-center mb-8">
          <button
            className="flex items-center gap-2 bg-white px-6 py-3 rounded shadow hover:shadow-lg border font-semibold text-lg"
            onClick={() => setShowMonthDropdown((v) => !v)}
          >
            <Sparkles className="w-5 h-5 text-primary" />
            {months[selectedMonth]}
            {showMonthDropdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showMonthDropdown && (
            <div className="absolute mt-14 bg-white rounded shadow-lg border z-10 w-64">
              {months.map((m, idx) => (
                <div
                  key={m + '-' + idx}
                  className={`px-5 py-3 cursor-pointer hover:bg-primary/10 ${selectedMonth === idx ? 'bg-primary/20 font-bold' : ''}`}
                  onClick={() => { setSelectedMonth(idx); setShowMonthDropdown(false); }}
                >
                  {m}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filters (Format) */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded shadow border">
            <Filter className="w-4 h-4 text-primary" />
            <span className="font-medium">Format</span>
            <select className="ml-2 bg-transparent outline-none" value={filters.format} onChange={e => setFilters(f => ({ ...f, format: e.target.value }))}>
              <option value="">All</option>
              {allFormats.map(format => <option key={format} value={format}>{format}</option>)}
            </select>
          </div>
        </div>

        {/* Interactive Calendar Grid */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="text-xl font-semibold text-gray-800 mb-6 text-center">
            UN Observance Days in {months[selectedMonth]}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {unDays
              .filter((d: any) => new Date(d.date).getMonth() === selectedMonth)
              .map((d: any) => (
                <button
                  key={d.name}
                  className="flex items-center gap-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-primary/10 rounded-lg px-6 py-4 shadow hover:shadow-lg hover:-translate-y-1 transition text-left w-full"
                  onClick={() => setSelectedDay(d)}
                >
                  <div className="flex-shrink-0 text-primary font-bold text-2xl">
                    {new Date(d.date).getDate().toString().padStart(2, '0')}
                  </div>
                  <div>
                    <div className="font-semibold text-lg">{d.name}</div>
                    <div className="text-xs text-gray-500">{d.date}</div>
                  </div>
                </button>
              ))}
          </div>
          {unDays.filter((d: any) => new Date(d.date).getMonth() === selectedMonth).length === 0 && (
            <div className="text-center text-gray-400 py-8">No observance days found for this month.</div>
          )}
        </div>

        {/* Details Panel for Selected Day */}
        {selectedDay && (
          <div className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center animate-fadeIn" onClick={() => setSelectedDay(null)}>
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full relative animate-scaleIn" onClick={e => e.stopPropagation()}>
              <button className="absolute top-3 right-3 text-gray-400 hover:text-primary" onClick={() => setSelectedDay(null)}>&times;</button>
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-6 h-6 text-primary" />
                <span className="font-bold text-xl">{selectedDay.name}</span>
              </div>
              <div className="mb-4 text-gray-600">{selectedDay.date}</div>
              <div className="mb-2 font-semibold">Campaign Ideas:</div>
              <div className="mb-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded p-2">
                Need help planning for any specific UN Day? <span className="underline cursor-pointer">Reach out to us.</span>
              </div>
              <CampaignIdeasCarousel
                ideas={getFilteredCampaigns(selectedDay)}
                expandedCards={expandedCards}
                setExpandedCards={setExpandedCards}
              />
              <div className="mt-4 text-xs text-gray-400">(AI-powered copy and more ideas coming soon!)</div>
            </div>
          </div>
        )}

        {/* Share/Email Modal */}
        <div className="flex justify-center">
          <button
            className="bg-primary text-white px-8 py-3 rounded-lg font-bold text-lg shadow hover:bg-primary/90 flex items-center gap-2"
            onClick={() => setShowEmailModal(true)}
          >
            <Mail className="w-5 h-5" /> Send Campaign Kit to Email
          </button>
        </div>
        {showEmailModal && (
          <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center animate-fadeIn" onClick={() => setShowEmailModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full relative animate-scaleIn" onClick={e => e.stopPropagation()}>
              <button className="absolute top-3 right-3 text-gray-400 hover:text-primary" onClick={() => setShowEmailModal(false)}>&times;</button>
              <div className="font-bold text-lg mb-4 flex items-center gap-2"><Mail className="w-5 h-5 text-primary" /> Email this Campaign Kit</div>
              <form onSubmit={handleEmailSend} className="space-y-4">
                <input
                  type="email"
                  required
                  placeholder="Enter your email address"
                  className="w-full border rounded px-4 py-2 focus:outline-primary"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                <button type="submit" className="w-full bg-primary text-white py-2 rounded font-semibold hover:bg-primary/90 transition">Send</button>
              </form>
              {emailSent && (
                <div className="mt-4 text-green-600 font-semibold animate-bounceIn">✅ Sent! Check your inbox.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
